/**
 * FEA-022: AI Assistant Context Provider
 *
 * Tracks the current page and selected items to provide context
 * to the AI chat panel. Pages call setters when selections change.
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useProject } from './ProjectContext';
import { useTerminology } from '../hooks/useTerminology';

// Hook to get route path to friendly page name mapping with dynamic terminology
function usePageNames(): Record<string, string> {
  const { t } = useTerminology();
  return useMemo(
    () => ({
      // sysview-wave2 — '/' and '/components' redirect to the Interlock shell now
      // (AI chat doesn't mount there); entries kept only for the redirect flash.
      '/': 'Components',
      '/components': 'Components',
      '/conduits': 'Conduits',
      '/commissioning': 'Commissioning Board',
      '/system-states': 'System States',
      '/cause-effect': 'Cause & Effect',
      '/risk-assessment': 'Risk Assessment',
      '/risk-dashboard': 'Risk Dashboard',
      '/visualizations': 'Visualizations',
      '/mitigations': t.mitigations,
      '/requirements': t.requirements,
      '/test-plans': 'Test Plans',
      '/power-analysis': 'Power Analysis',
      '/reports': 'Reports',
    }),
    [t.mitigations, t.requirements]
  );
}

export interface AISelection {
  componentId?: string | null;
  componentName?: string | null;
  failureModeId?: string | null;
  failureModeName?: string | null;
  causeId?: string | null;
  causeName?: string | null;
  mitigationId?: string | null;
  mitigationName?: string | null;
  effectId?: string | null;
  effectName?: string | null;
  conduitId?: string | null;
  conduitName?: string | null;
  requirementId?: string | null;
  requirementName?: string | null;
  scenarioId?: string | null;
  scenarioName?: string | null;
}

export interface AIContextData {
  page: string;
  pagePath: string;
  projectId: string | null;
  projectName: string | null;
  componentId?: string | null;
  componentName?: string | null;
  failureModeId?: string | null;
  failureModeName?: string | null;
  causeId?: string | null;
  causeName?: string | null;
  mitigationId?: string | null;
  mitigationName?: string | null;
  effectId?: string | null;
  effectName?: string | null;
  conduitId?: string | null;
  conduitName?: string | null;
  requirementId?: string | null;
  requirementName?: string | null;
  scenarioId?: string | null;
  scenarioName?: string | null;
}

interface AIContextType {
  /** Current context data for the AI assistant */
  context: AIContextData;

  /** Set the currently selected items (called by pages when selections change) */
  setSelection: (selection: AISelection) => void;

  /** Clear all selections (called when navigating away) */
  clearSelection: () => void;

  /** Get a formatted context string for display */
  getContextSummary: () => string;
}

const AIContext = createContext<AIContextType | undefined>(undefined);

export function AIContextProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { activeProject } = useProject();
  const pageNames = usePageNames();

  // Selection state (set by pages when items are selected)
  const [selection, setSelectionState] = useState<AISelection>({});

  // Derive page name from current route
  const pagePath = location.pathname;
  const page = pageNames[pagePath] || pagePath.split('/').pop() || 'Unknown';

  // Combined context data
  const context: AIContextData = useMemo(
    () => ({
      page,
      pagePath,
      projectId: activeProject?.id || null,
      projectName: activeProject?.name || null,
      ...selection,
    }),
    [page, pagePath, activeProject, selection]
  );

  // Set selection (called by pages)
  const setSelection = useCallback((newSelection: AISelection) => {
    setSelectionState((prev) => ({
      ...prev,
      ...newSelection,
    }));
  }, []);

  // Clear all selections
  const clearSelection = useCallback(() => {
    setSelectionState({});
  }, []);

  // Format context for display in the UI
  const getContextSummary = useCallback((): string => {
    const parts: string[] = [];

    parts.push(`Page: ${page}`);

    if (context.projectName) {
      parts.push(`Project: ${context.projectName}`);
    }

    if (context.componentName) {
      parts.push(`Component: ${context.componentName}`);
    }

    if (context.failureModeName) {
      parts.push(`Failure Mode: ${context.failureModeName}`);
    }

    if (context.causeName) {
      parts.push(`Cause: ${context.causeName}`);
    }

    if (context.mitigationName) {
      parts.push(`Mitigation: ${context.mitigationName}`);
    }

    if (context.effectName) {
      parts.push(`Effect: ${context.effectName}`);
    }

    if (context.conduitName) {
      parts.push(`Conduit: ${context.conduitName}`);
    }

    if (context.requirementName) {
      parts.push(`Requirement: ${context.requirementName}`);
    }

    if (context.scenarioName) {
      parts.push(`Scenario: ${context.scenarioName}`);
    }

    return parts.join(' > ');
  }, [page, context]);

  const value = useMemo(
    () => ({
      context,
      setSelection,
      clearSelection,
      getContextSummary,
    }),
    [context, setSelection, clearSelection, getContextSummary]
  );

  return <AIContext.Provider value={value}>{children}</AIContext.Provider>;
}

export function useAIContext() {
  const context = useContext(AIContext);
  if (!context) {
    throw new Error('useAIContext must be used within AIContextProvider');
  }
  return context;
}

/**
 * Hook to format context for sending to the bridge server.
 * Returns the context in the format expected by the bridge.
 */
export function useAIContextForBridge(): AIContextData {
  const { context } = useAIContext();
  return context;
}
